import cn from 'classnames'
import React from 'react'
import { useSelector } from 'react-redux'
import Button, { ButtonProps } from 'src/components/Button/Button'
import HoverableLink, {
  HoverableLinkProps,
} from 'src/components/HoverableLink/HoverableLink'
import {
  sidenavCollapsedWidth,
  sidenavExpandedWidth,
} from 'src/components/SidenavWrapper/sidenav.constants'
import Subnav, {
  SubnavDesign,
  SubnavOption,
} from 'src/components/Subnav/Subnav'
import {
  selectIsSidenavLockedOpenFromState,
  selectShowSidenavLargeFromState,
} from 'src/redux/selectors/app.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import { css } from 'styled-components'

import styles from './LayoutShell.style'

interface LayoutShellProps {
  subnavDesign: SubnavDesign
  subnavOptions: SubnavOption[]
  content: React.ReactElement
  title: React.ReactElement
  subtitle?: string
  buttons?: LayoutShellButton[]
  links?: HoverableLinkProps[]
  className?: string
  css?: ReturnType<typeof css>
  inert?: boolean
}

export type LayoutShellButton = ButtonProps | React.ReactElement

const isButtonProps = (button: LayoutShellButton): button is ButtonProps =>
  !React.isValidElement(button)

const LayoutShell: React.FC<LayoutShellProps> = (props): React.ReactElement => {
  const fixedElementPushdown = useSelector(selectFixedElementPushdown)
  const showSidenavLarge = useSelector(selectShowSidenavLargeFromState)
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)
  const rightShift = sideNavLockedOpen
    ? `${sidenavExpandedWidth}px`
    : `${sidenavCollapsedWidth}px`

  return (
    <div
      className={cn('LayoutShell', props.className, {
        sideNavSmall: !showSidenavLarge,
      })}
      css={css`
        top: ${fixedElementPushdown}px;
        width: calc(100% - ${rightShift});
        ${styles};
        ${props.css};
      `}
      {...(props.inert ? { inert: '' } : {})}
    >
      <div className="LayoutShell outerContainer">
        <div className="flex-column">
          <span className="LayoutShell title">{props.title}</span>
          {props.subtitle && (
            <span className="LayoutShell subtitle">{props.subtitle}</span>
          )}
        </div>
        {(props.links || props.buttons) && (
          <div className="flex-row" style={{ display: 'flex', gap: 24 }}>
            {props.links?.map((link, index) => (
              <HoverableLink key={`link-${index}`} {...link} />
            ))}
            {props.buttons?.map((button, index) =>
              isButtonProps(button) ? (
                <Button key={`button-${index}`} {...button} />
              ) : (
                React.cloneElement(button, {
                  key: `button-${index}`,
                })
              ),
            )}
          </div>
        )}
      </div>
      <div className="flex-column" style={{ gap: 12 }}>
        {props.subnavOptions.length > 1 && (
          <Subnav options={props.subnavOptions} design={props.subnavDesign} />
        )}
        {props.content}
      </div>
    </div>
  )
}

export default LayoutShell
